import type React from "react"

import { useAuthentication } from "@/contexts"
import { HStack } from "@/nzds/layout"
import { useFlags } from "@/services/feature"
import { singleLineEllipsisStyles } from "@/styles/utils"
import { isNzeroOrAsueneStaff } from "@/utils/user"

import { Box, Divider, Stack, Typography, useTheme } from "@mui/material"

import { ShareButton } from "../../share-button/share-button"
import { LanguageSwitcher } from "../language-switcher"
import { OrganizationNameSwitcher } from "../organization-name-switcher"
import { SiteSearch } from "../site-search/site-search"
import { UserMenu } from "../user-menu"

interface PageHeaderProps {
  id?: string
  title: string
}

export const PageHeader: React.FC<PageHeaderProps> = ({ id, title }) => {
  const { activeUser } = useAuthentication()
  const { flagsIsFetched, user_settings_language } = useFlags()
  const theme = useTheme()
  // KTD4 (PROD-10270): hide until flags are fetched so this control does not
  // flash when Settings cannot switch. Intentional exception to gating nav on
  // the flag only (`Boolean(flag)`), without `flagsIsFetched`.
  const showLanguageSwitcher =
    isNzeroOrAsueneStaff(activeUser) && flagsIsFetched && user_settings_language

  return (
    <Box
      sx={{
        bgcolor: "common.white",
        position: "sticky",
        top: 0,
        zIndex: "appBar",
      }}
    >
      <HStack
        component="header"
        sx={{
          alignItems: "center",
          height: 76,
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Stack
          sx={{
            alignItems: "flex-start",
            minWidth: 0,
            position: "relative",
          }}
        >
          <OrganizationNameSwitcher />
          <Typography
            data-e2e="page-header-title"
            id={id}
            sx={{
              ...singleLineEllipsisStyles,
              maxWidth: "100%",
              position: "relative",
              top: `calc(${theme.spacing(0.5)} * -1)`,
            }}
            variant="h1"
          >
            {title}
          </Typography>
        </Stack>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1,
          }}
        >
          <SiteSearch />
          <ShareButton iconButton />
          {showLanguageSwitcher ? <LanguageSwitcher compact /> : null}
          <UserMenu />
        </Box>
      </HStack>
      <Divider />
    </Box>
  )
}
